import type {
  ActiveSessionData,
  TimerCacheLike,
  TimerClientSessionPresence,
  TimerSessionLike,
} from "./types.js";
import { DEFAULT_BREAK_TIME, DEFAULT_FOCUS_TIME } from "./constants.js";

export interface BackendSessionPayload<TSession extends TimerSessionLike> {
  session?: TSession | null;
  isPaused?: boolean;
  durationSeconds?: number;
  duration?: number | string;
  currentIntervalStartTime?: string | number | Date | null;
}

export interface ActiveSessionFields<TSession extends TimerSessionLike> {
  session: TSession;
  isPaused: boolean;
  currentIntervalStartTime: string;
  durationSeconds: number;
  duration?: unknown;
}

export interface ClientSessionPatch {
  sessionId: string;
  isPaused?: boolean;
  tempFocusTime?: number;
  tempBreakTime?: number;
  intervalStartTime?: string;
}

/** Persisted client session shape used to seed timer display before `/active` returns. */
export interface ClientSessionLike {
  sessionId: string;
  startedAt: string;
  isPaused: boolean;
  intervalStartTime: string;
  tempFocusTime: number | null;
  tempBreakTime: number | null;
}

/**
 * Builds a minimal `ActiveSessionData` from persisted client session state so the
 * timer can render immediately while the server `/sessions/active` fetch (and its
 * null-retries) are still in flight.
 */
export function buildActiveSessionFromClientSession(
  client: ClientSessionLike,
  defaults?: { focusSeconds?: number; breakSeconds?: number },
  nowMs: number = Date.now(),
): ActiveSessionData {
  const focusFallback = defaults?.focusSeconds ?? DEFAULT_FOCUS_TIME * 60;
  const breakFallback = defaults?.breakSeconds ?? DEFAULT_BREAK_TIME * 60;
  const startedAt = normalizeIso(client.startedAt) ?? new Date(nowMs).toISOString();
  const intervalStartTime =
    normalizeIso(client.intervalStartTime) ?? startedAt;
  const startedMs = new Date(startedAt).getTime();
  const durationSeconds = Math.max(
    0,
    Math.floor((nowMs - startedMs) / 1000),
  );

  return {
    session: {
      id: client.sessionId,
      startedAt,
      endedAt: null,
      targetGoal: 0,
      finalPoints: 0,
      duration: durationSeconds,
      tasksWorkedOn: 0,
      tasksCompleted: 0,
      tempFocusTime: client.tempFocusTime ?? focusFallback,
      tempBreakTime: client.tempBreakTime ?? breakFallback,
      deviceId: "",
      isPaused: client.isPaused,
      currentIntervalStartTime: intervalStartTime,
    },
    durationSeconds,
    isPaused: client.isPaused,
    currentIntervalStartTime: intervalStartTime,
  };
}

export function normalizeIso(input: unknown): string | null {
  if (input == null) return null;
  if (typeof input === "string") return input;
  if (typeof input === "number") return new Date(input).toISOString();
  if (input instanceof Date) return input.toISOString();
  try {
    return new Date(String(input)).toISOString();
  } catch {
    return null;
  }
}

/**
 * Coerce in-session timer lengths (seconds) from API/PubNub payloads.
 * Some transports deliver numeric JSON fields as strings; without coercion,
 * UI checks like `typeof x === "number"` fail and fall back to profile defaults.
 */
export function coerceSessionTempSeconds(value: unknown): number | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/**
 * Read focus/break duration in seconds from session, with fallback when unset/invalid.
 */
export function readSessionTempSeconds(value: unknown, fallbackSeconds: number): number {
  const coerced = coerceSessionTempSeconds(value);
  if (coerced === null || coerced === undefined) return fallbackSeconds;
  return coerced;
}

function withCoercedSessionTemps<T extends TimerSessionLike>(session: T): T {
  let changed = false;
  const next: TimerSessionLike = { ...session };

  if (session.tempFocusTime !== undefined) {
    const tf = coerceSessionTempSeconds(session.tempFocusTime);
    if (tf !== undefined) {
      next.tempFocusTime = tf;
      changed = true;
    }
  }
  if (session.tempBreakTime !== undefined) {
    const tb = coerceSessionTempSeconds(session.tempBreakTime);
    if (tb !== undefined) {
      next.tempBreakTime = tb;
      changed = true;
    }
  }

  return changed ? (next as T) : session;
}

export function normalizeActiveSessionFields<TSession extends TimerSessionLike>(
  response: BackendSessionPayload<TSession> | undefined,
  prev: Pick<TimerCacheLike, "durationSeconds" | "currentIntervalStartTime"> & {
    duration?: unknown;
  } | null
): ActiveSessionFields<TSession> | null {
  const rawSession = response?.session;
  if (!rawSession?.id) return null;

  const session = withCoercedSessionTemps(rawSession) as TSession;

  const durationSeconds =
    typeof response?.durationSeconds === "number"
      ? response.durationSeconds
      : typeof response?.duration === "number"
      ? response.duration
      : prev?.durationSeconds ?? 0;

  const duration = response?.duration ?? prev?.duration;

  return {
    session,
    isPaused: Boolean(response?.isPaused),
    currentIntervalStartTime:
      normalizeIso(response?.currentIntervalStartTime) ??
      normalizeIso(prev?.currentIntervalStartTime) ??
      new Date().toISOString(),
    durationSeconds,
    duration,
  };
}

export function buildClientSessionPatchFromServer<TSession extends TimerSessionLike>(
  response: BackendSessionPayload<TSession> | null | undefined
): ClientSessionPatch | null {
  if (!response?.session?.id) return null;
  const patch: ClientSessionPatch = { sessionId: response.session.id };

  if (typeof response.isPaused === "boolean") {
    patch.isPaused = response.isPaused;
  }
  const tf = coerceSessionTempSeconds(response.session.tempFocusTime);
  if (typeof tf === "number") {
    patch.tempFocusTime = tf;
  }
  const tb = coerceSessionTempSeconds(response.session.tempBreakTime);
  if (typeof tb === "number") {
    patch.tempBreakTime = tb;
  }
  const intervalStartTime = normalizeIso(response.currentIntervalStartTime);
  if (intervalStartTime) {
    patch.intervalStartTime = intervalStartTime;
  }

  return patch;
}

export function deriveIsSessionActive(
  clientSession: TimerClientSessionPresence | null | undefined,
  serverSessionId: string | null | undefined,
  isClientSessionStale: boolean
): boolean {
  const hasFreshClientSession = Boolean(clientSession?.sessionId) && !isClientSessionStale;
  return hasFreshClientSession || Boolean(serverSessionId);
}
